import React, { useState, useEffect, useMemo } from 'react'
import CommonList from './CommonList'
import PropertiesList from './PropertiesList'
import TrashHeavy from '@tecuity/sos-icons/react/trash-heavy'
import './SettingsListView.scss'
import { ContextButton, TextInput } from '@tecuity/granite-ui'
import { ConfirmDialog } from '../../Modal/Modal'
import Portal from 'components/Portal/Portal'
import CrossHeavy from '@tecuity/sos-icons/react/cross-heavy'
import * as api from 'requests'
import { Spinner } from 'components/Loaders/Loaders'
import { noop } from 'utilities'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { setErrorToast, setSuccessToast } from '../../../actions'
import {
  SET_SETTINGS_LIST,
  SET_SETTINGS_ERROR,
  SET_SETTINGS_LOADING,
  SET_PAGE_PROPERTIES,
  SET_SETTINGS_FILTER_TEXT,
  SET_SETTINGS_PAGE_NUM,
} from '../SettingsReducer'
import ApplicationSettingsOptions from './Options/ApplicationSettingsOptions'
import CustomCorrespondenceOptions from './Options/CustomCorrespondenceOptions'
import RegulatedWordsOptions from './Options/RegulatedWordsOptions'
import CollateralKeywordOptions from './Options/CollateralKeywordOptions'
import WordManagementOptions from './Options/WordManagementOptions'
import UserDetailOptions from './Options/UserDetailOptions'
import SignerCapacityTypesOptions from './Options/SignerCapacityTypesOptions'
import TrademarkClassTypeOptions from './Options/TrademarkClassTypeOptions'
import { useDebounce } from '../../../Utilities/hooks'

const SettingsListView = (props) => {
  const {
    Redirect,
    AllowAdd = true,
    AllowDelete = false,
    Name = '',
    SettingsPageId,
    readOnly = false,
    // SourceTypeConfigurationId: 11,
  } = props
  const dispatch = useDispatch()
  const [selectedRow, setSelectedRow] = useState({})
  const [isAdding, setIsAdding] = useState(true)
  const [filterText, setFilterText] = useState('')
  const debouncedText = useDebounce(filterText, 250)
  const {
    settingsList,
    settingsError,
    optionalData,
    loading,
    // defaultDTOs,
    pageNum = 0,
  } = useSelector((state) => state.settings, shallowEqual)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleteAll, setIsDeleteAll] = useState(false)

  const nameAccessor = useMemo(() => {
    switch (Redirect) {
      case 'postalCodes':
        return 'Code'
      case 'customCorrespondence':
        return 'StaffDescription'
      case 'userDetails':
        return 'Username'
      default:
        return 'Name'
    }
  }, [Redirect])

  const optionsComponent = useMemo(() => {
    switch (Redirect) {
      case 'applicationSettings':
        return <ApplicationSettingsOptions />
      case 'customCorrespondence':
        return <CustomCorrespondenceOptions />
      case 'regulatedWords':
        return <RegulatedWordsOptions />
      case 'collateralKeywords':
        return <CollateralKeywordOptions />
      case 'wordManagement':
        return <WordManagementOptions />
      case 'userDetails':
        return <UserDetailOptions />
      case 'signerCapacityTypes':
        return <SignerCapacityTypesOptions />
      case 'trademarkClassTypes':
        return <TrademarkClassTypeOptions />
      default:
        return null
    }
  }, [Redirect])

  useEffect(() => {
    dispatch({ type: SET_SETTINGS_FILTER_TEXT, filterText: debouncedText })
  }, [debouncedText])

  const scrollPaginate = useMemo(() => {
    switch (Redirect) {
      case 'userDetails':
        return (e) => {
          const bottom =
            Math.abs(e.target.scrollHeight - (e.target.scrollTop + e.target.clientHeight)) <= 1
          if (bottom) {
            dispatch({ type: SET_SETTINGS_PAGE_NUM, pageNum: (pageNum || 0) + 1 })
          }
        }
      default:
        return () => {}
    }
  }, [Redirect, pageNum])

  const handleError = (action, err) => {
    console.error(`Error ${action} ${Redirect} settings list`, err)
    if (action === 'retrieving')
      dispatch({
        type: SET_SETTINGS_ERROR,
        settingsError: `Error ${action} ${Redirect} settings list`,
      })
    else dispatch(setErrorToast(`Error ${action} ${Redirect} settings list`))
    dispatch({ type: SET_SETTINGS_LOADING, loading: false })
  }

  const handleSuccess = (res) => {
    dispatch({ type: SET_SETTINGS_LIST, settingsList: res })
    dispatch({ type: SET_SETTINGS_LOADING, loading: false })
    dispatch({ type: SET_SETTINGS_ERROR, settingsError: '' })
  }

  useEffect(() => {
    if (!AllowAdd && settingsList.length > 0 && !selectedRow.Id) {
      selectRow(settingsList[0])
    }
  }, [AllowAdd, settingsList, selectedRow])

  const sortList = (res, property = 'Name') => {
    return res.sort((a, b) => a[property].localeCompare(b[property]))
  }

  const getList = () => {
    api.GET_SETTING_PROPERTIES_BY_PAGE_ID(
      SettingsPageId,
      (data) => {
        const defaultDTO = {}
        let pageProperties = data
        if (pageProperties.find((property) => property.SortOrder)){
          pageProperties = pageProperties.sort((a, b) => a.SortOrder - b.SortOrder)
        }
        pageProperties.forEach((property) => {
          if (property.IsActive || property.IsRequired) {
            switch (property.DefaultValueType) {
              case 'array':
                defaultDTO[property.Name] = JSON.parse(property.DefaultValue)
                break
              case 'bool':
                defaultDTO[property.Name] = property.DefaultValue === 'true' ? true : false
                break
              case 'datetime':
                defaultDTO[property.Name] = property.DefaultValue === 'null' ? null : new Date()
                break
              case 'int':
                defaultDTO[property.Name] = Number(property.DefaultValue)
                break
              case 'object':
                defaultDTO[property.Name] =
                  property.DefaultValue === `{ Id: '' }`
                    ? { Id: '' }
                    : JSON.parse(property.DefaultValue)
                break
              case 'string':
                defaultDTO[property.Name] = property.DefaultValue
                break
              default:
                console.warn(
                  `Property: ${property.Name} has invalid value type of ${property.DefaultValueType}.`
                )
                // defaultDTO[property.Name] = property.DefaultValue
                break
            }
          }
        })
        switch (SettingsPageId) {
          case 18: // Record User Access Configured Source Types
            defaultDTO['AppliesToIds'] = []
            defaultDTO['PasskeyDefaultIds'] = []
            defaultDTO['RequestAccessDefaultIds'] = []
            defaultDTO['LoggedInDefaultIds'] = []
            defaultDTO['NotLoggedInDefaultIds'] = []
            break
          default:
            break
        }
        dispatch({
          type: SET_PAGE_PROPERTIES,
          defaultDTO,
          pageProperties,
          pathParameter: Redirect,
        })
        //   {
        //     "SettingsPropertyId": 5001,
        //     "SettingsPageId": 50,
        //     "Name": "Name",
        //     "IsActive": true,
        //     "IsRequired": true,
        //     "SortOrder": null,
        //     "DefaultValue": "",
        //     "DefaultValueType": "string"
        // }
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to load Setting Properties config'))
      }
    )

    switch (Redirect) {
      case 'applicationSettings':
        api.GET_CONFIGURABLE_APP_SETTINGS(
          optionalData.category || 'All',
          (res) => handleSuccess(sortList(res.map((item) => ({ ...item, Id: item.AppSettingId })))),
          (err) => handleError('retrieving', err)
        )
        break
      case 'states':
        api.GET_SETTINGS_STATES(
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'countries':
        api.GET_SETTINGS_COUNTRIES(
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'counties':
        api.GET_SETTINGS_COUNTIES(
          (res) => handleSuccess(sortList(res.filter((item) => item.Id > 0))),
          (err) => handleError('retrieving', err)
        )
        break
      case 'postalCodes':
        api.GET_SETTINGS_POSTAL_CODES(
          (res) => handleSuccess(sortList(res, 'Code')),
          (err) => handleError('retrieving', err)
        )
        break
      case 'processedThroughDates':
        api.GET_SETTINGS_PROCESSED_THROUGH_DATES(
          (res) =>
            handleSuccess(
              sortList(res.map((item) => ({ ...item, Id: item.ProcessedThroughDateId })))
            ),
          (err) => handleError('retrieving', err)
        )
        break
      case 'notaryBondCompanies':
        api.GET_SETTINGS_NOTARY_BOND_COMPANIES(
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'complaintReasonTypes':
        api.GET_SETTINGS_NOTARY_COMPLAINT_REASONS(
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'notaryFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'NotaryFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'notaryRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'NotaryRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'uccRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'UCCRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'uccFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'UCCFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'uccTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'LienType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'uccStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'UCCStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'userXmlApi': //UCCXmlUserAccess
        api.GET_UCC_XML_USER_ACCESS_LIST(
          (res) => {
            handleSuccess(
              sortList(
                res.map((item) => {
                  const user = item.User
                  return {
                    Id: user.UserId,
                    Name: `${user.Username} (${user.UserId})`,
                  }
                })
              )
            )
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'businessXmlUserApi':
        api.GET_BUSINESS_XML_USER_ACCESS_LIST(
          (res) => {
            handleSuccess(
              sortList(
                res.map((item) => {
                  const user = item.User
                  return {
                    Id: user.UserId,
                    Name: `${user.Username} (${user.UserId})`,
                  }
                })
              )
            )
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'customCorrespondence':
        api.GET_SETTINGS_CUSTOM_CORRESPONDENCE_REASONS(
          optionalData.sourceType || 0,
          optionalData.recordType || 0,
          optionalData.recordSubType || 'null',
          optionalData.filingType || 0,
          (res) => handleSuccess(sortList(res, 'StaffDescription')),
          (err) => handleError('retrieving', err)
        )
        break
      case 'notaryStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'NotaryStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'userDetails':
        dispatch({ type: SET_SETTINGS_LOADING, loading: false })
        dispatch({ type: SET_SETTINGS_LIST, settingsList: [] })
        break
      case 'userRolesAndRights':
        api.GET_SETTINGS_ALL_ACCESS_CONTROL_ROLES(
          (res) => {
            handleSuccess(
              sortList(
                res.map((role) => {
                  return {
                    Id: role.RoleId,
                    Name: `${role.Category} - ${role.Name}`,
                  }
                })
              )
            )
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'notarySpecialServiceProviders':
        api.GET_SOFTWARE_PROVIDERS(
          (res) => {
            handleSuccess(res)
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'complaintStatusTypes':
        api.GET_SETTINGS_NOTARY_COMPLAINT_STATUS_TYPES(
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'receiptItems':
        api.GET_SETTINGS_RECEIPT_ITEM_TYPES(
          (res) => {
            handleSuccess(sortList(res))
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'generalLedgerAccounts':
        api.GET_ALL_GL_ACCOUNT_TYPES(
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'collateralKeywords':
        api.GET_SETTINGS_REGULATED_WORDS_BY_TYPES_AND_CATEGORY(
          2000, // UCC Filing SourceTypeId
          0,
          0,
          optionalData.wordType || 0,
          (res) =>
            handleSuccess(
              sortList(
                res.map((item) => ({
                  ...item,
                  Id: item.RegulatedWordId,
                  Name: `${item.RegulatedWordTypeName} - ${item.Word}`,
                }))
              )
            ),
          (err) => handleError('retrieving', err)
        )
        break
      case 'businessFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'BusinessFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'businessRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'BusinessRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'businessRecordSubTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'BusinessRecordSubType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'businessStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'BusinessStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'businessPartyTitle':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'BusinessPartyTitle',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'businessPartyType':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'BusinessPartyType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'regulatedWords':
        api.GET_SETTINGS_REGULATED_WORDS_BY_TYPES_AND_CATEGORY(
          169, // Business Source Type Id
          optionalData.recordType || 0,
          optionalData.recordSubType || 0,
          optionalData.wordType || 0,
          (res) =>
            handleSuccess(
              sortList(
                res.map((item) => ({
                  ...item,
                  Id: item.RegulatedWordId,
                  Name: `${item.RegulatedWordTypeName} - ${item.Word}`,
                }))
              )
            ),
          (err) => handleError('retrieving', err)
        )
        break
      case 'workViews':
        api.GET_WORKVIEW_OPTIONS(
          (workviews) => {
            handleSuccess(sortList(workviews))
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'wordManagement':
        api.GET_ALL_REPLACEMENT_WORDS(
          (res) =>
            handleSuccess(
              sortList(
                res.map((item) => ({
                  ...item,
                  Id: item.ReplacementWordId,
                  Name: item.MatchString,
                }))
              )
            ),
          (err) => handleError('retrieving', err)
        )
        break
      case 'imagePageType':
        api.GET_ALL_IMAGE_PAGE_TYPES(
          (res) => {
            handleSuccess(
              sortList(
                res.map((item) => ({
                  ...item,
                  Name: `${item.ImagePageTypeCategory} - ${item.Name}`,
                }))
              )
            )
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'orderFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'OrderFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'trademarkApplicantTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'TrademarkApplicantType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'trademarkClassTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'TrademarkClassType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'trademarkFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'TrademarkFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'trademarkRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'TrademarkRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'trademarkStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'TrademarkStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'signerCapacityTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'SignerCapacityType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'registeredAgentRecordTypes':
        api.GET_ALL_ENUM_LOOKUP_TYPE(
          'RegisteredAgentRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'registeredAgentFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'RegisteredAgentFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'registeredAgentStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'RegisteredAgentStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'athleteAgentRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'AthleteAgentRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'athleteAgentFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'AthleteAgentFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break      
      case 'athleteAgentStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'AthleteAgentStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'serviceOfProcessStatutes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'serviceOfProcessStatute',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'serviceOfProcessCourts':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'serviceOfProcessCourt',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'trademarkApplicantOrganizationTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'TrademarkApplicantOrgType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'formPermissionSettings':
        api.GET_ALL_ENUM_LOOKUP_TYPE(
          'FormDefinitionAccess',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'historicSOS':
        api.GET_ALL_HISTORIC_SOS(
          (res) => {
            handleSuccess(
              sortList(
                res.map((hsos) => ({
                  ...hsos,
                  Name: `${hsos.FirstName} ${hsos.LastName} (${hsos.StartDate} - ${
                    hsos.EndDate || 'Present'
                  })`,
                }))
              )
            )
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'recordUserAccess':
        api.GET_ALL_RECORD_USER_ACCESS_ROLES(
          (res) => {
            handleSuccess(sortList(res))
          },
          (err) => handleError('retrieving', err)
        )
        break
      case 'charityFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'CharityFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'charityStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'CharityStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'charityRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'CharityRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'contractorFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ContractorFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'contractorStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ContractorStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'contractorRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ContractorRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'contractorClassTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ContractorClassType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'contractorComplaintTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ContractorComplaintType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'contractorComplaintStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ContractorComplaintStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'contractorComplaintReasonTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'ContractorComplaintReasonType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'lobbyistFilingTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'LobbyistFilingType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break
      case 'lobbyistStatusTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'LobbyistStatusType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'lobbyistRecordTypes':
        api.GET_ALL_BASIC_LOOKUP_TYPES(
          'LobbyistRecordType',
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      case 'miscellaneousModules':
        api.GET_MISCELLANEOUS_MODULES(          
          (res) => handleSuccess(sortList(res)),
          (err) => handleError('retrieving', err)
        )
        break;
      default:
        dispatch({ type: SET_SETTINGS_LOADING, loading: false })
        dispatch({
          type: SET_SETTINGS_ERROR,
          settingsError: `Error ${Redirect} Endpoints NOT tied in. This should not show up.`,
        })
    }
  }

  useEffect(() => {
    dispatch({ type: SET_SETTINGS_LIST, settingsList: [] })
    getList()
    return () => {
      dispatch({ type: SET_SETTINGS_LIST, settingsList: [] })
    }
  }, [])

  const selectRow = (row) => {
    setIsAdding(false)
    setSelectedRow(row)
  }

  const handleSaveSuccess = (newRow) => {
    setSelectedRow(newRow)
    dispatch(setSuccessToast('Saved Setting Successfully!'))
    setIsAdding(true)
    dispatch({ type: SET_SETTINGS_LOADING, loading: true })
    getList()
  }

  const deleteSuccess = () => {
    dispatch({ type: SET_SETTINGS_LOADING, loading: true })
    setIsAdding(true)
    dispatch(setSuccessToast(`Successfully Deleted Setting${isDeleteAll ? 's' : ''}!`))
    getList()
  }

  const finishDelete = () => {
    switch (Redirect) {
      case 'counties':
        api.DELETE_SETTINGS_COUNTIES(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'countries':
        api.DELETE_SETTINGS_COUNTRIES(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'notaryBondCompanies':
        api.DELETE_SETTINGS_NOTARY_BOND_COMPANY(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'complaintReasonTypes':
        api.DELETE_SETTINGS_NOTARY_COMPLAINT_REASONS(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'complaintStatusTypes':
        api.DELETE_SETTINGS_NOTARY_COMPLAINT_STATUS(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'customCorrespondence':
        api.DELETE_SETTINGS_CUSTOM_CORRESPONDENCE_REASONS(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'postalCodes':
        api.DELETE_SETTINGS_POSTAL_CODES(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'states':
        api.DELETE_SETTINGS_STATES(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'collateralKeywords':
      case 'regulatedWords':
        api.DELETE_SETTINGS_REGULATED_WORDS(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'userXmlApi':
        if (isDeleteAll) {
          dispatch(setErrorToast('Revoke All Access Not Allowed'))
          return
        }
        api.DELETE_SETTINGS_USER_XML_API_ACCESS(selectedRow.Id, deleteSuccess, (err) => {
          handleError('deleting from', err)
        })
        break
      case 'businessXmlUserApi':
        if (isDeleteAll) {
          dispatch(setErrorToast('Revoke All Access Not Allowed'))
          return
        }
        api.DELETE_SETTINGS_BUSINESS_XML_USER_ACCESS(selectedRow.Id, deleteSuccess, (err) => {
          handleError('deleting from', err)
        })
        break
      case 'workViews':
        api.DELETE_SETTINGS_WORK_VIEWS(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'wordManagement':
        api.DELETE_SETTINGS_REPLACEMENT_WORDS(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'processedThroughDates':
        api.DELETE_PROCESSED_THROUGH_DATE(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      case 'signerCapacityTypes':
        api.DELETE_SETTINGS_SIGNER_CAPACITY_TYPE(
          isDeleteAll ? settingsList.map((item) => item.Id) : [selectedRow.Id],
          deleteSuccess,
          (err) => {
            handleError('deleting from', err)
          }
        )
        break
      default:
        dispatch({
          type: SET_SETTINGS_ERROR,
          settingsError: `Error ${Redirect} delete Endpoint NOT tied in.`,
        })
        dispatch({ type: SET_SETTINGS_LOADING, loading: false })
    }
  }

  return (
    <div className="settings-list-view">
      <h2>{Name ? Name : Redirect.replace(/([A-Z])/g, ' $1').trim()}</h2>
      {loading ? (
        <Spinner size="large" themeColor="dark-neutral" />
      ) : (
        <React.Fragment>
          {optionsComponent}
          <div className="settings-content">
            <div className="settings-left-column">
              <TextInput
                customCss={`input {border: none}`}
                placeholder="Filter"
                onChange={(e) => setFilterText(e.target.value)}
              />
              {AllowDelete && (
                <div
                  className="delete-all"
                  onClick={() => {
                    setIsDeleteAll(true)
                    setShowDeleteModal(true)
                  }}
                >
                  Delete All <TrashHeavy size={30} />
                </div>
              )}
              <div className="settings-list-wrapper">
                {settingsError ? (
                  <div className="error">{settingsError}</div>
                ) : (
                  <React.Fragment>
                    <CommonList
                      allowDelete={AllowDelete}
                      selectItem={(row) => selectRow(row)}
                      deleteItem={(row) => {
                        setShowDeleteModal(true)
                        setIsDeleteAll(false)
                      }}
                      handleScroll={scrollPaginate}
                      list={(settingsList || [])
                        .filter((item) =>
                          (item[nameAccessor] || '')
                            .toLowerCase()
                            .includes(filterText.toLowerCase())
                        )
                        .map((item) => ({
                          Id: item.Id,
                          Name:
                            Redirect === 'postalCodes'
                              ? `${item[nameAccessor]} (${item.City})`
                              : item[nameAccessor],
                        }))}
                    />
                    {AllowAdd && !readOnly && (
                      <ContextButton variant="secondary" onClick={() => setIsAdding(true)}>
                        <CrossHeavy />
                      </ContextButton>
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
            <PropertiesList
              itemId={isAdding ? null : selectedRow.Id}
              allowAdd={AllowAdd}
              isAdding={isAdding}
              redirect={Redirect}
              handleSaveSuccess={handleSaveSuccess}
              readOnly={readOnly}
            />
          </div>
        </React.Fragment>
      )}
      <Portal isOpened={showDeleteModal}>
        <ConfirmDialog
          closeModal={() => {
            setShowDeleteModal(false)
          }}
          onModalClosed={noop}
          message={`Are you sure you want to delete ${isDeleteAll ? `All` : selectedRow.Name}?`}
          buttons={[
            { label: 'No', onClick: noop, className: 'btn-default' },
            {
              label: 'Yes',
              onClick: () => {
                finishDelete()
              },
              className: 'btn-danger',
            },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
    </div>
  )
}

export default SettingsListView
